<template>
  <div class="user-progress-tracker">
    <!-- Filters -->
    <div class="flex flex-wrap gap-4 mb-5 items-center">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        label="Search by user or course"
        outlined
        dense
        hide-details
        class="max-w-xs"
        clearable
      />
      <v-select
        v-model="filterCourse"
        :items="courseOptions"
        label="Filter by Course"
        outlined
        dense
        hide-details
        clearable
        class="max-w-xs"
      />
      <v-select
        v-model="filterDepartment"
        :items="departmentOptions"
        label="Filter by Department"
        outlined
        dense
        hide-details
        clearable
        class="max-w-xs"
      />
      <v-spacer />
      <v-chip color="#21427d" dark small class="font-semibold">
        <v-icon left small>mdi-account-group</v-icon>
        {{ filteredRows.length }} Records
      </v-chip>
    </div>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredRows"
      :items-per-page="10"
      class="progress-table elevation-1 rounded-lg"
      :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
      sort-by="progress"
      :sort-desc="true"
    >
      <!-- User column -->
      <template v-slot:item.userName="{ item }">
        <div class="flex items-center gap-3 py-2">
          <v-avatar size="34" color="#21427d">
            <span class="white--text font-bold text-sm">{{ getInitials(item.userName) }}</span>
          </v-avatar>
          <div>
            <div class="font-semibold text-sm">{{ item.userName }}</div>
            <div class="text-xs text-gray-500">{{ item.department }}</div>
          </div>
        </div>
      </template>

      <!-- Course column -->
      <template v-slot:item.courseTitle="{ item }">
        <div class="font-medium text-sm">{{ item.courseTitle }}</div>
      </template>

      <!-- Progress column -->
      <template v-slot:item.progress="{ item }">
        <div class="flex items-center gap-3 min-w-[180px]">
          <v-progress-linear
            :value="item.progress"
            :color="getProgressColor(item.progress)"
            height="8"
            rounded
            class="flex-1"
            background-color="#e8eaf6"
          />
          <span
            class="text-xs font-bold min-w-[40px] text-right"
            :style="{ color: getProgressColor(item.progress) }"
          >{{ item.progress }}%</span>
        </div>
      </template>

      <!-- Lessons column -->
      <template v-slot:item.completedLessons="{ item }">
        <v-chip small :color="item.completedLessons === item.totalLessons ? '#4caf50' : '#e3f2fd'" :text-color="item.completedLessons === item.totalLessons ? 'white' : '#21427d'">
          <v-icon left x-small>mdi-book-check</v-icon>
          {{ item.completedLessons }} / {{ item.totalLessons }}
        </v-chip>
      </template>

      <!-- Last Activity column -->
      <template v-slot:item.lastActivity="{ item }">
        <span class="text-sm text-gray-600" v-if="item.lastActivity">{{ formatDate(item.lastActivity) }}</span>
        <span class="text-sm text-gray-400 italic" v-else>No activity</span>
      </template>

      <!-- Status column -->
      <template v-slot:item.status="{ item }">
        <v-chip
          x-small
          :color="getStatusColor(item.progress)"
          dark
          class="font-semibold"
        >
          {{ getStatusLabel(item.progress) }}
        </v-chip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { state, getUserProgressTable } from './learningData'

export default {
  name: 'UserProgressTracker',
  data() {
    return {
      searchQuery: '',
      filterCourse: null,
      filterDepartment: null,
      headers: [
        { text: 'User', value: 'userName', sortable: true },
        { text: 'Course', value: 'courseTitle', sortable: true },
        { text: 'Progress', value: 'progress', sortable: true },
        { text: 'Lessons', value: 'completedLessons', sortable: true, align: 'center' },
        { text: 'Last Activity', value: 'lastActivity', sortable: true },
        { text: 'Status', value: 'status', sortable: false, align: 'center' }
      ]
    }
  },
  computed: {
    allRows() {
      // Access state.progress to track reactivity
      void state.progress.length
      return getUserProgressTable()
    },
    courseOptions() {
      return [...new Set(this.allRows.map(r => r.courseTitle))]
    },
    departmentOptions() {
      return [...new Set(this.allRows.map(r => r.department))]
    },
    filteredRows() {
      let rows = this.allRows
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        rows = rows.filter(r =>
          r.userName.toLowerCase().includes(q) ||
          r.courseTitle.toLowerCase().includes(q) ||
          r.userEmail.toLowerCase().includes(q)
        )
      }
      if (this.filterCourse) {
        rows = rows.filter(r => r.courseTitle === this.filterCourse)
      }
      if (this.filterDepartment) {
        rows = rows.filter(r => r.department === this.filterDepartment)
      }
      return rows
    }
  },
  methods: {
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    },
    getProgressColor(progress) {
      if (progress >= 100) return '#4caf50'
      if (progress >= 60) return '#2196f3'
      if (progress >= 30) return '#ff9800'
      return '#f44336'
    },
    getStatusLabel(progress) {
      if (progress >= 100) return 'Completed'
      if (progress >= 60) return 'On Track'
      if (progress > 0) return 'In Progress'
      return 'Not Started'
    },
    getStatusColor(progress) {
      if (progress >= 100) return '#4caf50'
      if (progress >= 60) return '#2196f3'
      if (progress > 0) return '#ff9800'
      return '#9e9e9e'
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
.progress-table {
  border: 1px solid #e0e0e0;
}
.progress-table >>> .v-data-table-header th {
  background: #f5f7fa !important;
  font-weight: 700 !important;
  color: #21427d !important;
  font-size: 0.8rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.progress-table >>> tbody tr:hover {
  background: #f0f4ff !important;
}
</style>
